import React from 'react';
import { Sequence, useCurrentFrame, useVideoConfig } from 'remotion';
import { ThemeSystemConfig } from '../../clipCreator/remotion/Components/Theme/Theme';
import { AspectRatio } from '../AspectRatio';
import { TimelineDefaults } from '../TimelineDefaults';

export interface LayerItem {
    id: string;
    startAt: number;
    duration?: number;
    render(fps: number, theme: ThemeSystemConfig, aspectRatio: AspectRatio, defaults: TimelineDefaults, videoConfig?: { width: number; height: number }): React.ReactNode;
}

export const wrapInSequence = (
    layer: LayerItem,
    fps: number,
    children: React.ReactNode,
    namePrefix?: string,
    animationInfo?: string
): React.ReactNode => {
    const endTime = layer.duration ? layer.startAt + layer.duration : '∞';
    const animationLabel = animationInfo ? `[${animationInfo}]` : '';
    
    // CRITICAL FIX: Always provide durationInFrames with minimum 1 frame
    // Remotion requires positive durationInFrames, cannot be 0
    // This prevents "durationInFrames must be positive, but got 0" error
    const durationInFrames = layer.duration && layer.duration > 0
        ? Math.max(1, Math.round(layer.duration * fps))  // Enforce minimum 1 frame
        : undefined;  // Omit property if no duration (infinite sequence)

    const sequenceProps = {
        key: layer.id,
        name: `${namePrefix || 'Layer'}-${layer.id}-${layer.startAt}s→${endTime}s${animationLabel}`,
        from: Math.round(layer.startAt * fps),
        ...(durationInFrames !== undefined ? { durationInFrames } : {})
    };

    return (
        <Sequence {...sequenceProps}>
            {children}
        </Sequence>
    );
};

/**
 * Create a dynamic sequence wrapper that changes name based on animation state
 */
export const createDynamicSequence = (
    baseName: string,
    item: { id: string; startAt: number; duration?: number; entranceAnimation?: any; exitAnimation?: any },
    sequenceProps: any,
    fps: number
): React.FC<{ children: React.ReactNode }> => {
    return ({ children }) => {
        const currentFrame = useCurrentFrame();
        const remotionVideoConfig = useVideoConfig();
        const consistentFps = remotionVideoConfig.fps;
        const globalFrame = currentFrame + Math.round(item.startAt * consistentFps);
        const currentTime = globalFrame / consistentFps;
        
        // Determine current animation state
        let animationState = 'VISIBLE';
        let activeAnimation = '';
        
        // Check entrance animation
        if (item.entranceAnimation) {
            const animationStartTime = item.startAt - item.entranceAnimation.timing.duration;
            const animationEndTime = item.startAt;
            if (currentTime >= animationStartTime && currentTime <= animationEndTime) {
                animationState = 'ENTRANCE';
                activeAnimation = item.entranceAnimation.type;
            }
        }
        
        // Check exit animation
        if (item.exitAnimation && item.duration) {
            const contentEndTime = item.startAt + item.duration;
            const animationStartTime = contentEndTime - item.exitAnimation.timing.duration; // Exit animations start BEFORE content ends
            const animationEndTime = contentEndTime + item.exitAnimation.timing.duration; // Extend AFTER content end for symmetry
            if (currentTime >= animationStartTime && currentTime <= animationEndTime) {
                animationState = 'EXIT';
                activeAnimation = item.exitAnimation.type;
            }
        }
        
        // Create dynamic name
        const endTime = item.duration ? item.startAt + item.duration : '∞';
        const timeInfo = `${item.startAt}s→${endTime}${typeof endTime === 'number' ? 's' : ''}`;
        const stateLabel = animationState !== 'VISIBLE' ? `-${animationState}-${activeAnimation}` : '';
        const dynamicName = `${baseName}-${item.id}-${timeInfo}${stateLabel}`;
        
        // Update sequence props with dynamic name
        const dynamicSequenceProps = {
            ...sequenceProps,
            name: dynamicName
        };
        
        return (
            <Sequence {...dynamicSequenceProps}>
                {children}
            </Sequence>
        );
    };
}; 